import React from "react";

import ChannelGlyph from "./ChannelGlyph";

/**
 * One research source, with what it contributes and the queries routed to it.
 *
 * RESEARCH-EXPLORATION-REDESIGN, 2026-09-10. This replaces a column of
 * identical full-width rows in which every query looked the same and nothing
 * said where it would be sent.
 *
 * EVERYTHING HERE COMES FROM THE SERVER. The name, the description and the
 * source it routes to are the service's `KIND_CATALOGUE` — the same sentences
 * the proposer prompt is given, so the operator and the model are told the same
 * thing. The screen that this replaces hardcoded four sources the service had no
 * adapter for.
 *
 * THE COVERAGE INDICATOR IS NOT A SCORE. Founder ruling: nothing on screen may
 * be a placeholder. So it reports two real facts — how many queries route here,
 * and what this source returned on the last round — and when no round has run it
 * says that, rather than drawing an empty bar that reads as zero.
 */
export default function SourceLane({
  lane, editable, onChangeQuery, onRemoveQuery, onAddQuery, onToggle, busy,
  onRetry,
}) {
  const [retryDraft, setRetryDraft] = React.useState("");
  const labelled = lane.queries.filter((q) => (q.label || "").trim()).length;
  const ran = lane.lastRun?.ran;
  const returned = lane.lastRun?.items ?? 0;

  /* A FAILED SOURCE IS NOT AN EMPTY ONE. Founder, 2026-09-11: "if the non
     required elements fail/have no input/output then that must be indicated on
     the screen otherwise I won't know."
     This lane had three states and chose "returned nothing last run" whenever
     `items` was 0 — so a timeout, a 404 and a genuine empty answer all read the
     same. Measured across 22 real rounds and 128 source calls: 43 failed and 0
     succeeded-but-returned-nothing, so every "returned nothing" ever shown here
     was a failure. `outcome` now comes from the hook, computed from the `ok`
     flag each result row has always carried, and the source's own recorded
     reason is shown verbatim. */
  const outcome = lane.lastRun?.outcome;          // ok | partial | empty | failed
  const offTopic = lane.lastRun?.offTopic ?? 0;  // judged about another subject
  const repeats = lane.lastRun?.repeats ?? 0;    // rows that were the same creative
  const maxVariants = lane.lastRun?.maxVariants ?? 0;
  const failedCalls = lane.lastRun?.failedCalls ?? 0;
  const calls = lane.lastRun?.calls ?? 0;
  const errors = lane.lastRun?.errors || [];
  const why = errors.length ? errors.join("; ") : null;

  // A FAULT and a THIN ANSWER are different colours. `crit` is a failure —
  // something broke and the round is missing data it should have had. `warn` is
  // a real but empty answer, or a query missing before a run. Neither is used
  // for "not run yet", which an empty meter would otherwise read as zero.
  const health = !ran
    ? (lane.selected && labelled === 0 ? "warn" : "")
    : outcome === "failed" || outcome === "partial" ? "crit"
    : outcome === "empty" ? "warn"
    : "";

  const lastText = !ran
    ? (lane.selected && labelled === 0 ? "no query — will contribute nothing" : "not run yet")
    : outcome === "failed"
      ? `failed last run${why ? ` — ${why}` : ""}`
      : outcome === "partial"
        ? `${failedCalls} of ${calls} calls failed${why ? ` — ${why}` : ""}`
        : outcome === "empty"
          ? "ran, worked, found nothing"
          : `${returned} result${returned === 1 ? "" : "s"} last run`;

  return (
    <section className={`lane${lane.selected ? "" : " is-off"}`}
             data-testid={`lane-${lane.kind}`}>
      <header className="lane-head">
        <div className="lane-id">
          {/* The channel's own mark, so a source is recognisable before it is
              read. The tile carries the only saturated colour in the lane. */}
          <span className={`lane-glyph k-${lane.kind.toLowerCase()}`} aria-hidden="true">
            <ChannelGlyph kind={lane.kind} />
          </span>
          <div className="lane-idtext">
            <h4 className="lane-name">{lane.label}</h4>
            <p className="lane-what">{lane.contributes}</p>
          </div>
        </div>

        <div className="lane-right">
          <div className="lane-cover" data-testid={`lane-cover-${lane.kind}`}>
            <span className="lane-count">
              <b>{labelled}</b> quer{labelled === 1 ? "y" : "ies"}
            </span>
            <div className={`meter ${health}`} aria-hidden="true">
              <b style={{ width: labelled ? "100%" : "0%" }} />
            </div>
            {/* `role="status"` so a reader is told the outcome changed, and
                the fault case is announced rather than sitting silently in
                small grey text. */}
            <span className={`lane-last${health ? ` is-${health}` : ""}`}
                  role="status"
                  data-outcome={ran ? outcome : "not-run"}
                  data-testid={`lane-last-${lane.kind}`}>
              {lastText}
            </span>
          </div>
          <button type="button"
                  className={`lane-toggle${lane.selected ? " on" : ""}`}
                  onClick={() => onToggle && onToggle(lane.source)}
                  disabled={!onToggle || busy}
                  aria-pressed={lane.selected}
                  aria-label={`${lane.label}: ${lane.selected ? "included in" : "excluded from"} this research`}
                  data-testid={`lane-toggle-${lane.kind}`}>
            <span className="lane-toggle-dot" aria-hidden="true" />
            <span className="lane-toggle-text">
              {lane.selected ? "included" : "excluded"}
            </span>
          </button>
        </div>
      </header>

      {/* LOUD. Founder, 2026-09-11: "if the query returns nothing, it says so
          loudly on the page so the human can then make an edit and see if that
          would return anything back."
          The outcome line in the header is 10px and right-aligned — fine as a
          status, useless as news. When a source comes back with nothing or
          fails, it is stated here, in the body, at reading size, with the one
          action that fixes it. And it is only offered once a round has run,
          because before that there is nothing to retry. */}
      {ran && (outcome === "empty" || outcome === "failed" || outcome === "partial") ? (
        <div className={`lane-alarm is-${health}`} role="status"
             data-testid={`lane-alarm-${lane.kind}`}>
          <p className="lane-alarm-what">
            {outcome === "empty"
              ? `This source ran and found nothing for “${lane.queries
                  .map((q) => q.label).filter(Boolean).join("”, “")}”.`
              : outcome === "partial"
                ? `${failedCalls} of ${calls} calls to this source failed.`
                : "This source failed."}
            {why ? <> The reason it gave: <b>{why}</b>.</> : null}
          </p>
          {outcome === "empty" ? (
            <p className="lane-alarm-why">
              That is an answer, not a fault — there are no live ads or results
              matching those words. A shorter, plainer term usually finds them.
            </p>
          ) : null}

          {onRetry ? (
            <div className="lane-retry">
              <label className="sr-only" htmlFor={`retry-${lane.kind}`}>
                A different query for {lane.label}
              </label>
              <input id={`retry-${lane.kind}`} className="lane-input"
                     value={retryDraft}
                     onChange={(e) => setRetryDraft(e.target.value)}
                     placeholder="Try a different query…"
                     data-testid={`lane-retry-input-${lane.kind}`} />
              <button type="button" className="btn-primary btn-sm"
                      disabled={busy || !retryDraft.trim()}
                      onClick={() => onRetry(lane.kind, retryDraft.trim())}
                      data-testid={`lane-retry-${lane.kind}`}>
                Retry this source
              </button>
            </div>
          ) : null}
          <p className="lane-alarm-cost">
            Sends this one query to this one source. It does not re-run the
            others and does not use up a research round.
          </p>
        </div>
      ) : null}

      {/* WHAT THE GATE EXCLUDED, and on what basis. Nothing was deleted — these
          results are still in the record — but they are kept out of the brief,
          so the operator must be able to see it happened. An exclusion the
          operator cannot see is indistinguishable from a gate that is broken. */}
      {/* REPEATS. Five rows were one creative, five times — so the count above
          is distinct ads, and this says what was collapsed. The repeat count is
          kept rather than discarded: an advertiser running one creative many
          times is spending on it, which is evidence it works. */}
      {ran && repeats > 0 ? (
        <p className="lane-repeats" data-testid={`lane-repeats-${lane.kind}`}>
          <b>{repeats}</b> further row{repeats === 1 ? " was" : "s were"} the
          same creative run again, counted once.
          {maxVariants > 1 ? (
            <> The most-repeated ad here is running <b>{maxVariants}&times;</b>.</>
          ) : null}
        </p>
      ) : null}

      {ran && offTopic > 0 ? (
        <p className="lane-excluded" data-testid={`lane-excluded-${lane.kind}`}>
          <b>{offTopic}</b> result{offTopic === 1 ? "" : "s"} excluded as being
          about a different subject, and kept out of the brief.
          {lane.lastRun?.onTopic ? null : (
            <> Nothing from this source reached the brief.</>
          )}
        </p>
      ) : null}

      {lane.queries.length === 0 ? (
        <p className="lane-empty" data-testid={`lane-empty-${lane.kind}`}>
          No query routes here yet.
        </p>
      ) : (
        <ul className="lane-queries">
          {lane.queries.map((q) => (
            <li key={q.target_id || q.index} data-testid={`lane-q-${lane.kind}-${q.index}`}>
              {editable ? (
                <>
                  {/* A REAL LABEL, not a placeholder. The screen this replaces
                      had placeholder-only inputs, which is the skill's
                      highest-severity form rule and unusable with a reader. */}
                  <label className="sr-only" htmlFor={`q-${lane.kind}-${q.index}`}>
                    {lane.label} query
                  </label>
                  <input id={`q-${lane.kind}-${q.index}`}
                         className="lane-input"
                         value={q.label}
                         onChange={(e) => onChangeQuery(q.index, e.target.value)}
                         disabled={busy}
                         data-testid={`lane-input-${lane.kind}-${q.index}`} />
                  <button type="button" className="iconbtn"
                          onClick={() => onRemoveQuery(q.index)}
                          disabled={busy}
                          aria-label={`Remove this ${lane.label} query`}
                          data-testid={`lane-remove-${lane.kind}-${q.index}`}>
                    ✕
                  </button>
                </>
              ) : (
                <span className="lane-chip">
                  {q.label}
                  {/* WHERE IT CAME FROM, from the stored field. Absent on the
                      documents written before origins were recorded, and shown
                      as unknown there rather than guessed. */}
                  {q.origin === "SUGGESTED" ? (
                    <em className="lane-origin" data-testid={`lane-origin-suggested-${q.index}`}>suggested</em>
                  ) : q.origin === "EDITED" ? (
                    <em className="lane-origin is-edited" data-testid={`lane-origin-edited-${q.index}`}>edited</em>
                  ) : q.origin === "ADDED" ? (
                    <em className="lane-origin is-edited" data-testid={`lane-origin-added-${q.index}`}>yours</em>
                  ) : q.origin === "UNKNOWN" ? (
                    <em className="lane-origin is-unknown" data-testid={`lane-origin-unknown-${q.index}`}>
                      origin not recorded
                    </em>
                  ) : null}
                </span>
              )}
              {q.rationale && !editable ? (
                <p className="lane-why">{q.rationale}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editable ? (
        <button type="button" className="lane-add"
                onClick={() => onAddQuery(lane.kind)}
                disabled={busy}
                data-testid={`lane-add-${lane.kind}`}>
          + Add a {lane.label.toLowerCase()} query
        </button>
      ) : null}
    </section>
  );
}
